/**
 * Where the divider in the screener editor's Columns tab was left.
 *
 * ## Why localStorage
 *
 * A panel split is view state, the same kind of thing as `group-selection.ts`
 * and `interval-mru.ts` — and the contract has nowhere to put it. `ScreenerConfig`
 * records the columns and their widths, never how tall the box you edit them in
 * happened to be. Adding a field for it would mean a schema change, codegen on
 * two platforms, and a column in qv-data's shredded store, all to remember one
 * number that belongs to a person rather than to a screen.
 *
 * Deliberately **not** keyed by section, for `interval-mru`'s reason: "how much
 * room I want for writing expressions" is a habit, not a property of one
 * screener, so two screeners sharing one split is the useful behaviour rather
 * than a collision. If the contract ever grows a per-user preferences object,
 * this belongs in it and should sync.
 *
 * Every storage path is failure-tolerant — Safari private mode throws on
 * `localStorage` access, and a divider is not worth a blank app.
 */

const KEY = "qufolio.editor.columnsSplit";

/**
 * The stored split, or `undefined` to let the builder use its own default.
 *
 * `undefined` rather than a number, so the fallback lives in exactly one place:
 * the component that draws the divider. A default repeated here would be a
 * second answer to "how tall is the editor" that nothing keeps in step.
 */
export function loadColumnsSplit(): number | undefined {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw === null) return undefined;
    const value = Number.parseFloat(raw);
    // A stored value is only as trustworthy as whatever last wrote it — another
    // tab, an older build, or a hand-edited devtools entry. Out-of-range reads
    // as absent rather than as a pane dragged shut.
    return Number.isFinite(value) && value > 0 && value < 1 ? value : undefined;
  } catch {
    return undefined;
  }
}

/** Remember the split. Called on drag release, never per pointer move. */
export function saveColumnsSplit(split: number): void {
  try {
    if (!Number.isFinite(split)) return;
    window.localStorage.setItem(KEY, String(split));
  } catch {
    // Storage refused. The split still applies for this session; it simply does
    // not outlive it, which is the same behaviour as before it was stored.
  }
}
